import { useState, useEffect, useCallback, memo } from 'react';
import { Modal, Input, Switch } from 'antd';
import Sketch from '@uiw/react-color-sketch';
import { Editor } from '@hugerte/hugerte-react';
import { CARD_TYPES } from '../cardTypes';

const { TextArea } = Input;

const CardSettingsModal = memo(
	({ visible, cardData, cardType, onSave, onCancel, currentUser }) => {
		const [settings, setSettings] = useState({
			backgroundColor: '#3b82f6',
			borderColor: '#2563eb',
			textColor: '#ffffff',
			showBorder: true,
			borderSize: 2,
			link: '',
			title: 'Get Started',
			subtitle: 'Click the button below to begin',
			buttonText: 'Go Now',
			buttonBackground: '#ffffff',
			buttonTextColor: '#3b82f6',
			referralsBackgroundColor: '#ffffff',
			referralsBorderColor: '#ffffff',
			referralsTextColor: '#000000',
			referralsTotalColor: '#0686d0',
			bonusesBackgroundColor: '#6cb761',
			bonusesBorderColor: '#6cb761',
			bonusesTextColor: '#ffffff',
			bonusesTotalColor: '#ffffff',
			statColor: '#000000',
			arrowUpColor: '#19bb4b',
			arrowDownColor: '#ff4d4d',
			htmlContent: '',
			hideTitle: false,
			removePadding: false,
			announcementsAmount: 3,
			jobsAmount: 3,
			showHotJobsOnly: true,
			announcementPosition: 1,
			shoutoutsLimit: 3,
			badgesAmount: 2,
			showSubCompanyFilter: false,
		});

		const [activeColorPicker, setActiveColorPicker] = useState(null);
		const [useRichTextEditor, setUseRichTextEditor] = useState(true);

		useEffect(() => {
			if (visible && cardData) {
				setSettings({
					backgroundColor: cardData.backgroundColor || '#3b82f6',
					borderColor: cardData.borderColor || '#2563eb',
					textColor: cardData.textColor || '#ffffff',
					showBorder: cardData.showBorder !== false,
					borderSize: cardData.borderSize || 2,
					link: cardData.link || '',
					title: cardData.title || 'Get Started',
					subtitle: cardData.subtitle || 'Click the button below to begin',
					buttonText: cardData.buttonText || 'Go Now',
					buttonBackground: cardData.buttonBackground || '#ffffff',
					buttonTextColor: cardData.buttonTextColor || '#3b82f6',
					showReferralsRanking: cardData.showReferralsRanking !== false,
					showPointsRanking: cardData.showPointsRanking !== false,
					referralsBackgroundColor:
						cardData.referralsBackgroundColor || '#ffffff',
					referralsBorderColor: cardData.referralsBorderColor || '#ffffff',
					referralsTextColor: cardData.referralsTextColor || '#000000',
					referralsTotalColor: cardData.referralsTotalColor || '#0686d0',
					bonusesBackgroundColor: cardData.bonusesBackgroundColor || '#6cb761',
					bonusesBorderColor: cardData.bonusesBorderColor || '#6cb761',
					bonusesTextColor: cardData.bonusesTextColor || '#ffffff',
					bonusesTotalColor: cardData.bonusesTotalColor || '#ffffff',
					statColor: cardData.statColor || '#000000',
					arrowUpColor: cardData.arrowUpColor || '#19bb4b',
					arrowDownColor: cardData.arrowDownColor || '#ff4d4d',
					htmlContent: cardData.htmlContent || '',
					hideTitle: cardData.hideTitle || false,
					removePadding: cardData.removePadding || false,
					announcementsAmount: cardData.announcementsAmount || 3,
					jobsAmount: cardData.jobsAmount || 3,
					showHotJobsOnly: cardData.showHotJobsOnly !== false,
					announcementPosition: cardData.announcementPosition || 1,
					shoutoutsLimit: cardData.shoutoutsLimit || 3,
					badgesAmount: cardData.badgesAmount || 2,
					showSubCompanyFilter: cardData.showSubCompanyFilter || false,
				});
			}
		}, [visible, cardData]);

		const handleChange = useCallback((field, value) => {
			setSettings((prev) => ({ ...prev, [field]: value }));
		}, []);

		const handleColorChange = useCallback((field, color) => {
			setSettings((prev) => ({ ...prev, [field]: color.hex }));
		}, []);

		const handleSave = useCallback(() => {
			onSave(settings);
			setActiveColorPicker(null);
		}, [settings, onSave]);

		const handleCancel = useCallback(() => {
			onCancel();
			setActiveColorPicker(null);
		}, [onCancel]);

		const renderColorPicker = (field, label) => {
			const isActive = activeColorPicker === field;

			return (
				<div className="mb-4">
					<label className="block text-sm font-medium text-gray-700 mb-2">
						{label}
					</label>
					<div className="flex items-center gap-2">
						<div
							className="w-10 h-10 rounded border-2 border-gray-300 cursor-pointer hover:border-blue-500 transition-colors"
							style={{ backgroundColor: settings[field] }}
							onClick={() => setActiveColorPicker(isActive ? null : field)}
						/>
						<Input
							value={settings[field]}
							onChange={(e) => handleChange(field, e.target.value)}
							className="flex-1"
							placeholder="#000000"
						/>
					</div>
					{isActive && (
						<div className="mt-2">
							<Sketch
								color={settings[field]}
								onChange={(color) => handleColorChange(field, color)}
								disableAlpha
								style={{ boxShadow: 'none' }}
							/>
						</div>
					)}
				</div>
			);
		};

		return (
			<Modal
				title="Card Settings"
				open={visible}
				onOk={handleSave}
				onCancel={handleCancel}
				width={800}
				okText="Save"
				cancelText="Cancel"
				destroyOnClose
				data-no-drag
			>
				<div
					className="space-y-6"
					data-no-drag
					onPointerDown={(e) => e.stopPropagation()}
				>
					<div className="border-b pb-4">
						<h3
							className="text-sm font-semibold text-gray-800 mb-4"
							style={{ fontSize: '20px', fontWeight: 700, color: '#666666' }}
						>
							General Settings
						</h3>
						<div className="space-y-4">
							<div className="mb-4">
								<label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
									<Switch
										checked={settings.showBorder}
										onChange={(checked) => handleChange('showBorder', checked)}
									/>
									<span style={{ marginLeft: '6px' }}>Show Border</span>
								</label>
							</div>
							{settings.showBorder && (
								<div className="mb-4">
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Border Size (px)
									</label>
									<Input
										type="number"
										min="1"
										max="10"
										value={settings.borderSize}
										onChange={(e) =>
											handleChange('borderSize', parseInt(e.target.value) || 2)
										}
										placeholder="2"
									/>
								</div>
							)}
							{cardType === CARD_TYPES.LEADERBOARD && (
								<>
									<div className="mb-4">
										<label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
											<Switch
												checked={settings.showReferralsRanking}
												onChange={(checked) =>
													handleChange('showReferralsRanking', checked)
												}
											/>
											<span style={{ marginLeft: '6px' }}>
												Show Referrals Ranking
											</span>
										</label>
									</div>

									<div className="mb-4">
										<label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
											<Switch
												checked={settings.showPointsRanking}
												onChange={(checked) =>
													handleChange('showPointsRanking', checked)
												}
											/>
											<span style={{ marginLeft: '6px' }}>
												Show Points Ranking
											</span>
										</label>
									</div>
								</>
							)}
							{cardType === CARD_TYPES.CUSTOM_HTML && (
								<>
									<div className="mb-4">
										<label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
											<Switch
												checked={settings.hideTitle}
												onChange={(checked) =>
													handleChange('hideTitle', checked)
												}
											/>
											<span style={{ marginLeft: '6px' }}>Hide Title</span>
										</label>
									</div>

									<div className="mb-4">
										<label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
											<Switch
												checked={settings.removePadding}
												onChange={(checked) =>
													handleChange('removePadding', checked)
												}
											/>
											<span style={{ marginLeft: '6px' }}>Remove Padding</span>
										</label>
									</div>
								</>
							)}
							{cardType === CARD_TYPES.ANNOUNCEMENTS && (
								<div className="mb-4">
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Number of Announcements to Display
									</label>
									<Input
										type="number"
										min="1"
										max="10"
										value={settings.announcementsAmount}
										onChange={(e) =>
											handleChange(
												'announcementsAmount',
												parseInt(e.target.value) || 3
											)
										}
										placeholder="3"
									/>
									<p className="text-xs text-gray-500 mt-1">
										Choose how many recent announcements to show (1-10)
									</p>
								</div>
							)}
							{cardType === CARD_TYPES.RECENT_JOBS && (
								<>
									<div className="mb-4">
										<label className="block text-sm font-medium text-gray-700 mb-2">
											Number of Recent Jobs
										</label>
										<Input
											type="number"
											min="1"
											max="10"
											value={settings.jobsAmount}
											onChange={(e) =>
												handleChange(
													'jobsAmount',
													parseInt(e.target.value) || 3
												)
											}
											placeholder="3"
										/>
										<p className="text-xs text-gray-500 mt-1">
											Choose how many recent jobs to show
										</p>
									</div>

									<div className="mb-4">
										<label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
											<Switch
												checked={settings.showHotJobsOnly}
												onChange={(checked) =>
													handleChange('showHotJobsOnly', checked)
												}
											/>
											<span style={{ marginLeft: '6px' }}>
												Show Hot Jobs Only
											</span>
										</label>
										<p className="text-xs text-gray-500 mt-1">
											When enabled, only jobs marked as "Hot Jobs" will be
											displayed
										</p>
									</div>
								</>
							)}
							{cardType === CARD_TYPES.RECOGNITION_SHOUTOUTS && (
								<div className="mb-4">
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Number of Recognition Shoutouts
									</label>
									<Input
										type="number"
										min="1"
										max="10"
										value={settings.shoutoutsLimit}
										onChange={(e) =>
											handleChange(
												'shoutoutsLimit',
												parseInt(e.target.value) || 2
											)
										}
										placeholder="2"
									/>
									<p className="text-xs text-gray-500 mt-1">
										Choose how many recognition shoutouts to show (1-10)
									</p>
								</div>
							)}
							{cardType === CARD_TYPES.MY_RECOGNITIONS && (
								<div className="mb-4">
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Number of Recognitions to Display
									</label>
									<Input
										type="number"
										min="1"
										max="10"
										value={settings.badgesAmount}
										onChange={(e) =>
											handleChange(
												'badgesAmount',
												parseInt(e.target.value) || 2
											)
										}
										placeholder="2"
									/>
									<p className="text-xs text-gray-500 mt-1">
										Choose how many recent recognitions to show (1-10)
									</p>
								</div>
							)}
							{cardType === CARD_TYPES.RECOGNITION_LEADERBOARD && (
								<div className="mb-4">
									<label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
										<Switch
											checked={settings.showSubCompanyFilter}
											onChange={(checked) =>
												handleChange('showSubCompanyFilter', checked)
											}
										/>
										<span style={{ marginLeft: '6px' }}>
											Show My {currentUser?.company?.subCompanyLabel || 'SubCompany'} Tab
										</span>
									</label>
								</div>
							)}
							{cardType === CARD_TYPES.ANNOUNCEMENT_THUMBNAIL && (
								<div className="mb-4">
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Select Announcement
									</label>
									<Input
										type="number"
										min="1"
										max="10"
										value={settings.announcementPosition}
										onChange={(e) =>
											handleChange(
												'announcementPosition',
												parseInt(e.target.value) || 1
											)
										}
										placeholder="1"
									/>
									<p className="text-xs text-gray-500 mt-1">
										Choose which announcement to display (1 = most recent, 10 =
										10th most recent)
									</p>
								</div>
							)}
						</div>
					</div>
					{cardType !== CARD_TYPES.ANNOUNCEMENT_THUMBNAIL && (
						<div className="border-b pb-4">
							<h3
								className="text-lg font-semibold text-gray-800 mb-4"
								style={{ fontSize: '20px', fontWeight: 700, color: '#666666' }}
							>
								Text Settings
							</h3>
							<div className="space-y-4">
								<div className="mb-4">
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Title
									</label>
									<Input
										value={settings.title}
										onChange={(e) => handleChange('title', e.target.value)}
										placeholder="Title"
									/>
								</div>
								{cardType === CARD_TYPES.CALL_TO_ACTION && (
									<>
										<div className="mb-4">
											<label className="block text-sm font-medium text-gray-700 mb-2">
												Subtitle
											</label>
											<Input
												value={settings.subtitle}
												onChange={(e) =>
													handleChange('subtitle', e.target.value)
												}
												placeholder="Subtitle"
											/>
										</div>

										<div className="mb-4">
											<label className="block text-sm font-medium text-gray-700 mb-2">
												Button Text
											</label>
											<Input
												value={settings.buttonText}
												onChange={(e) =>
													handleChange('buttonText', e.target.value)
												}
												placeholder="Button Text"
											/>
										</div>

										<div className="mb-4">
											<label className="block text-sm font-medium text-gray-700 mb-2">
												External Link
											</label>
											<Input
												value={settings.link}
												onChange={(e) => handleChange('link', e.target.value)}
												placeholder="https://example.com"
											/>
											<p className="text-xs text-gray-500 mt-1">
												Enter an external URL (e.g., https://example.com,
												https://yoursite.com/page)
											</p>
										</div>
									</>
								)}
								{cardType === CARD_TYPES.CUSTOM_HTML && (
									<div className="mb-4">
										<div className="flex items-center justify-between mb-2">
											<label className="block text-sm font-medium text-gray-700">
												HTML Content
											</label>
											<label className="flex items-center gap-2 text-sm font-medium text-gray-700">
												<span className="mr-2">Use Rich Text Editor</span>
												<Switch
													checked={useRichTextEditor}
													onChange={(checked) => setUseRichTextEditor(checked)}
												/>
											</label>
										</div>
										{useRichTextEditor ? (
											<div style={{ minHeight: 250 }}>
												<Editor
													init={{
														height: 250,
														menubar: false,
														branding: false,
														plugins: ['image', 'code', 'media', 'link'],
														toolbar:
															'undo redo | styles | fontfamily | bold italic forecolor backcolor | ' +
															'alignleft aligncenter alignright alignjustify | ' +
															'bullist numlist outdent indent | ' +
															'media link image',
														default_link_target: '_blank',
														automatic_uploads: true,
														file_picker_types: 'image',
														content_style:
															"@import url('https://fonts.googleapis.com/css?family=Open+Sans:400,400i,700,700i'); " +
															"body { font-family: 'Open Sans', sans-serif; } " +
															'img { max-width: 100%; } ' +
															'a {color: #018DD3; text-decoration: none !important;}',
														font_family_formats:
															'Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica,sans-serif; Impact=impact,chicago; Open Sans=open sans,helvetica neue,helvetica,arial,sans-serif; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva;',
														font_formats:
															'Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica neue,helvetica; Impact=impact,chicago; Open Sans=open sans,helvetica neue,helvetica,arial,sans-serif; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva;',
														relative_urls: false,
														convert_urls: false,
														remove_script_host: false,
														setup: function (editor) {
															editor.on('init', function () {
																editor.execCommand(
																	'FontName',
																	false,
																	'Open Sans'
																);
															});
														},
													}}
													value={settings.htmlContent || ''}
													onEditorChange={(value) =>
														handleChange('htmlContent', value)
													}
												/>
											</div>
										) : (
											<TextArea
												rows={10}
												value={settings.htmlContent || ''}
												onChange={(e) =>
													handleChange('htmlContent', e.target.value)
												}
												placeholder="Enter HTML code here..."
											/>
										)}
									</div>
								)}
							</div>
						</div>
					)}
					<div>
						<h3
							className="text-lg font-semibold text-gray-800 mb-4"
							style={{ fontSize: '20px', fontWeight: 700, color: '#666666' }}
						>
							Color Settings
						</h3>
						<div className="grid grid-cols-2 gap-4">
							<div>
								{renderColorPicker('backgroundColor', 'Background Color')}
							</div>
							<div>{renderColorPicker('textColor', 'Text Color')}</div>

							{settings.showBorder && (
								<div className="col-span-2">
									{renderColorPicker('borderColor', 'Border Color')}
								</div>
							)}

							{cardType === CARD_TYPES.CALL_TO_ACTION && (
								<>
									<div>
										{renderColorPicker('buttonBackground', 'Button Background')}
									</div>
									<div>
										{renderColorPicker('buttonTextColor', 'Button Text')}
									</div>
								</>
							)}

							{cardType === CARD_TYPES.YOUR_REFERRALS && (
								<>
									<div>
										{renderColorPicker(
											'referralsBackgroundColor',
											'Referrals Box Background'
										)}
									</div>
									<div>
										{renderColorPicker(
											'referralsBorderColor',
											'Referrals Box Border'
										)}
									</div>
									<div>
										{renderColorPicker(
											'referralsTextColor',
											'Referrals Label Text'
										)}
									</div>
									<div>
										{renderColorPicker(
											'referralsTotalColor',
											'Referrals Total'
										)}
									</div>
									<div>
										{renderColorPicker(
											'bonusesBackgroundColor',
											'Bonuses Box Background'
										)}
									</div>
									<div>
										{renderColorPicker(
											'bonusesBorderColor',
											'Bonuses Box Border'
										)}
									</div>
									<div>
										{renderColorPicker(
											'bonusesTextColor',
											'Bonuses Label Text'
										)}
									</div>
									<div>
										{renderColorPicker('bonusesTotalColor', 'Bonuses Total')}
									</div>
								</>
							)}

							{(cardType === CARD_TYPES.POINTS_RANKING ||
								cardType === CARD_TYPES.REFERRALS_RANKING ||
								cardType === CARD_TYPES.OPEN_JOBS) && (
								<div className="col-span-2">
									{renderColorPicker('statColor', 'Stat Value Color')}
								</div>
							)}

							{cardType === CARD_TYPES.REFERRALS_RANKING && (
								<>
									<div>
										{renderColorPicker('arrowUpColor', 'Arrow Up Color')}
									</div>
									<div>
										{renderColorPicker('arrowDownColor', 'Arrow Down Color')}
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			</Modal>
		);
	}
);

CardSettingsModal.displayName = 'CardSettingsModal';

export default CardSettingsModal;
